import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Package,
  Truck,
  CreditCard,
  Mail,
  Copy,
  Check,
  Clock,
  Printer,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminOrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAdminAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fulfillmentModal, setFulfillmentModal] = useState(false);
  const [fulfillmentData, setFulfillmentData] = useState({
    tracking_number: '',
    carrier: 'USPS',
    provider: 'manual'
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [rates, setRates] = useState([]);
  const [shipmentId, setShipmentId] = useState(null);

  const fetchOrder = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/orders/${orderId}`, { headers });
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const handleMarkPaid = async () => {
    if (!window.confirm('Mark this order as paid? This will also deduct inventory.')) return;
    
    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/orders/${orderId}/mark-paid`, {}, { headers });
      setOrder(response.data);
      toast.success('Order marked as paid');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFulfill = async () => {
    if (!fulfillmentData.tracking_number) {
      toast.error('Please enter a tracking number');
      return;
    }

    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/admin/orders/${orderId}/fulfill`,
        fulfillmentData,
        { headers }
      );
      setOrder(response.data);
      setFulfillmentModal(false);
      toast.success('Order marked as fulfilled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fulfill order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/orders/${orderId}/cancel`, {}, { headers });
      setOrder(response.data);
      toast.success('Order cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGetRates = async () => {
    setLabelLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/admin/shipping/rates`,
        { order_id: orderId },
        { headers }
      );
      setRates(response.data.rates || []);
      setShipmentId(response.data.shipment_id);
      setShowRates(true);
      toast.success(`Found ${response.data.rates?.length || 0} shipping rates`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get shipping rates');
    } finally {
      setLabelLoading(false);
    }
  };

  const handleCreateLabel = async (rateId = null, service = 'Priority') => {
    setLabelLoading(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        order_id: orderId,
        service: service
      };
      
      if (shipmentId && rateId) {
        payload.shipment_id = shipmentId;
        payload.rate_id = rateId;
      }
      
      const response = await axios.post(
        `${API}/admin/shipping/create-label`,
        payload,
        { headers }
      );
      
      setOrder(prev => ({
        ...prev,
        status: 'fulfilled',
        fulfillment: {
          carrier: response.data.carrier,
          tracking_number: response.data.tracking_number,
          service: response.data.service,
          label_url: response.data.label_url,
          shipped_at: new Date().toISOString()
        }
      }));
      
      setShowRates(false);
      toast.success(`Label created! Tracking: ${response.data.tracking_number}`);
      
      // Open label in new tab
      if (response.data.label_url) {
        window.open(response.data.label_url, '_blank');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create label');
    } finally {
      setLabelLoading(false);
    }
  };

  const handleQuickLabel = async () => {
    if (!window.confirm('Create USPS Priority Mail label automatically?')) return;
    await handleCreateLabel(null, 'Priority');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getStatusColor = (status) => {
    const colors = {
      payment_pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      fulfilled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      refunded: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#888] mb-4">Order not found</p>
        <Link to="/admin/orders">
          <Button variant="outline" className="border-[#3a3a3a] text-white">
            Back to Orders
          </Button>
        </Link>
      </div>
    );
  }

  const shipping = order.shipping || {};
  const totals = order.totals || { subtotal: order.subtotal, shipping: order.shipping_cost, total: order.total };
  const fulfillment = order.fulfillment || {};

  return (
    <div className="p-6 lg:p-8" data-testid="admin-order-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/orders">
            <Button variant="ghost" size="icon" className="text-[#888] hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              Order {order.order_id || order.id.slice(0, 8)}
              <span className={`text-sm px-3 py-1 rounded border ${getStatusColor(order.status)}`}>
                {order.status?.replace('_', ' ')}
              </span>
            </h1>
            <p className="text-[#888] text-sm">
              Created {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {/* Packing Slip - always available */}
          <Link to={`/admin/orders/${orderId}/packing-slip`}>
            <Button
              variant="outline"
              className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Packing Slip
            </Button>
          </Link>
          {order.status === 'payment_pending' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <Clock className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400">Awaiting Stripe payment</span>
            </div>
          )}
          {order.status === 'paid' && (
            <>
              <Button
                onClick={handleQuickLabel}
                disabled={labelLoading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="create-label-btn"
              >
                {labelLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Truck className="w-4 h-4 mr-2" />
                )}
                Create Label
              </Button>
              <Button
                onClick={handleGetRates}
                disabled={labelLoading}
                variant="outline"
                className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
                data-testid="get-rates-btn"
              >
                Compare Rates
              </Button>
              <Button
                onClick={() => setFulfillmentModal(true)}
                disabled={actionLoading}
                variant="outline"
                className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
                data-testid="manual-fulfill-btn"
              >
                Manual Entry
              </Button>
            </>
          )}
          {!['fulfilled', 'cancelled', 'refunded'].includes(order.status) && user?.role === 'admin' && (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={actionLoading}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              data-testid="cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Order Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a]">
            <div className="p-4 border-b border-[#2a2a2a]">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-[#D4540A]" />
                Order Items
              </h2>
            </div>
            <div className="divide-y divide-[#2a2a2a]">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-[#666] text-sm">
                      SKU: {item.sku || item.product_id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">
                      ${(item.unit_price || item.price || 0).toFixed(2)} × {item.qty || item.quantity}
                    </p>
                    <p className="text-[#888] font-medium">
                      ${((item.unit_price || item.price || 0) * (item.qty || item.quantity || 1)).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {/* Totals */}
            <div className="p-4 border-t border-[#2a2a2a] bg-[#151515]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-[#888]">
                  <span>Subtotal</span>
                  <span>${(totals.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#888]">
                  <span>Shipping</span>
                  <span>${(totals.shipping || 0).toFixed(2)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Discount</span>
                    <span>-${totals.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-semibold text-base pt-2 border-t border-[#2a2a2a]">
                  <span>Total</span>
                  <span>${(totals.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
            <h2 className="font-semibold text-white flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#D4540A]" />
              Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                <div>
                  <p className="text-white text-sm">Order Created</p>
                  <p className="text-[#666] text-xs">{new Date(order.created_at).toLocaleString()}</p>
                </div>
              </div>
              {order.status !== 'payment_pending' && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="text-white text-sm">Payment Confirmed</p>
                    <p className="text-[#666] text-xs">{order.updated_at ? new Date(order.updated_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
              )}
              {order.status === 'fulfilled' && fulfillment.shipped_at && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  <div>
                    <p className="text-white text-sm">Shipped via {fulfillment.carrier}</p>
                    <p className="text-[#666] text-xs">{new Date(fulfillment.shipped_at).toLocaleString()}</p>
                    <p className="text-[#888] text-xs mt-1">Tracking: {fulfillment.tracking_number}</p>
                  </div>
                </div>
              )}
              {order.status === 'cancelled' && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-2" />
                  <div>
                    <p className="text-white text-sm">Order Cancelled</p>
                    <p className="text-[#666] text-xs">{order.updated_at ? new Date(order.updated_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Customer */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
            <h2 className="font-semibold text-white mb-4">Customer</h2>
            <div className="space-y-3">
              <div>
                <p className="text-white font-medium">
                  {order.customer_name || `${shipping.first_name} ${shipping.last_name}`}
                </p>
                <button
                  onClick={() => copyToClipboard(order.email || shipping.email, 'Email')}
                  className="text-[#888] text-sm hover:text-white flex items-center gap-1"
                >
                  {order.email || shipping.email}
                  <Copy className="w-3 h-3" />
                </button>
                {(order.phone || shipping.phone) && (
                  <p className="text-[#666] text-sm">{order.phone || shipping.phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-white">Shipping Address</h2>
              <button
                onClick={() => copyToClipboard(
                  `${shipping.first_name} ${shipping.last_name}\n${shipping.address_line1}\n${shipping.address_line2 ? shipping.address_line2 + '\n' : ''}${shipping.city}, ${shipping.state} ${shipping.zip_code}\n${shipping.country}`,
                  'Address'
                )}
                className="text-[#666] hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[#ccc] text-sm space-y-1">
              <p>{shipping.first_name} {shipping.last_name}</p>
              <p>{shipping.address_line1}</p>
              {shipping.address_line2 && <p>{shipping.address_line2}</p>}
              <p>{shipping.city}, {shipping.state} {shipping.zip_code}</p>
              <p>{shipping.country}</p>
            </div>
          </div>

          {/* Fulfillment Info */}
          {order.status === 'fulfilled' && fulfillment.tracking_number && (
            <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
              <h2 className="font-semibold text-white mb-4">Shipment</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#888]">Carrier</span>
                  <span className="text-white">{fulfillment.carrier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#888]">Tracking</span>
                  <button
                    onClick={() => copyToClipboard(fulfillment.tracking_number, 'Tracking number')}
                    className="text-[#D4540A] hover:underline flex items-center gap-1"
                  >
                    {fulfillment.tracking_number}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Internal Notes */}
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
            <h2 className="font-semibold text-white mb-2">Internal Notes</h2>
            <p className="text-[#888] text-sm">
              {order.notes || 'No notes'}
            </p>
          </div>
        </div>
      </div>

      {/* Fulfillment Modal */}
      {fulfillmentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] w-full max-w-md">
            <div className="p-4 border-b border-[#2a2a2a] flex items-center justify-between">
              <h3 className="font-semibold text-white">Fulfill Order</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setFulfillmentModal(false)}
                className="text-[#888] hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-[#ccc] text-sm">Carrier</Label>
                <select
                  value={fulfillmentData.carrier}
                  onChange={(e) => setFulfillmentData({ ...fulfillmentData, carrier: e.target.value })}
                  className="w-full mt-1 bg-[#111] border border-[#3a3a3a] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#D4540A]"
                >
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                </select>
              </div>
              <div>
                <Label className="text-[#ccc] text-sm">Tracking Number</Label>
                <Input
                  value={fulfillmentData.tracking_number}
                  onChange={(e) => setFulfillmentData({ ...fulfillmentData, tracking_number: e.target.value })}
                  placeholder="Enter tracking number"
                  className="mt-1 bg-[#111] border-[#3a3a3a] text-white placeholder:text-[#555]"
                  data-testid="tracking-number-input"
                />
              </div>
            </div>
            <div className="p-4 border-t border-[#2a2a2a] flex gap-3">
              <Button
                variant="outline"
                onClick={() => setFulfillmentModal(false)}
                className="flex-1 border-[#3a3a3a] text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFulfill}
                disabled={actionLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-fulfill-btn"
              >
                {actionLoading ? 'Processing...' : 'Mark Fulfilled'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rates Comparison Modal */}
      {showRates && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-[#2a2a2a] flex items-center justify-between">
              <h3 className="font-semibold text-white">Select Shipping Rate</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowRates(false)}
                className="text-[#888] hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {rates.length === 0 ? (
                <p className="text-[#888] text-center py-8">No rates available</p>
              ) : (
                <div className="space-y-3">
                  {rates.map((rate, index) => (
                    <button
                      key={rate.id}
                      onClick={() => handleCreateLabel(rate.id)}
                      disabled={labelLoading}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        index === 0 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-[#3a3a3a] hover:border-[#555] bg-[#111]'
                      }`}
                      data-testid={`rate-option-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white">{rate.carrier}</span>
                            <span className="text-[#888]">{rate.service}</span>
                            {index === 0 && (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                                Best Value
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#888] mt-1">
                            {rate.delivery_days 
                              ? `${rate.delivery_days} business day${rate.delivery_days > 1 ? 's' : ''}` 
                              : 'Delivery estimate unavailable'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-white">${rate.rate.toFixed(2)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#2a2a2a]">
              <p className="text-xs text-[#666] text-center">
                Click a rate to create the shipping label and send notification email
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderDetailPage;
